/**
 * Extract API Platform constraint violations from an Axios-style error envelope.
 *
 * API Platform serializes a `ValidationException` (HTTP 422) as a JSON-LD payload:
 *
 * ```json
 * {
 *   "violations": [
 *     { "propertyPath": "ingredient_reference",
 *       "message": "...",
 *       "code": "INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE",
 *       "hint": { "i18nKey": "...", "supplierName": "...", "referenceName": "..." }
 *     }
 *   ]
 * }
 * ```
 *
 * The `code` value is the canonical machine-readable identifier we use on the
 * frontend to swap UI for the specific business case (e.g. switch the QSS form
 * for an explanation when the supplier-reference shadows it).
 */
export type ApiViolationHint = {
    severity?: "info" | "warning" | "error";
    i18nKey?: string;
    referenceName?: string;
    supplierName?: string;
} & Record<string, unknown>;

export type ApiViolation = {
    propertyPath?: string;
    message?: string;
    code?: string | null;
    hint?: ApiViolationHint;
};

type AxiosLikeError = {
    response?: {
        status?: number;
        data?: {
            violations?: ApiViolation[];
            "hydra:description"?: string;
            "hydra:title"?: string;
        };
    };
};

/**
 * Returns the violation array when the error is a 422 ConstraintViolationList,
 * `null` otherwise. Designed to be safe to call on `unknown` (e.g. react-query's
 * `error` is typed as `unknown`/`Error`).
 */
export const getApiViolations = (error: unknown): ApiViolation[] | null => {
    if (!error || typeof error !== "object") return null;
    const axiosErr = error as AxiosLikeError;
    if (axiosErr.response?.status !== 422) return null;
    const violations = axiosErr.response?.data?.violations;
    if (!Array.isArray(violations)) return null;
    return violations;
};

export const findViolationByCode = (error: unknown, code: string): ApiViolation | null => {
    const violations = getApiViolations(error);
    if (!violations) return null;
    return violations.find((v) => v.code === code) ?? null;
};

/**
 * Backend-defined error code for "the supplier has an IngredientReference whose
 * scores already cover the active campaign — there is no SSQ to fill". Mirrors
 * `SsqPostCreateProvider::ERROR_INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE`
 * exactly. Keeping the constant in one place makes the cross-stack contract
 * grep-friendly.
 */
export const INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE = "INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE" as const;

type ApiErrorEnvelope = {
    response?: {
        status?: number;
        data?: {
            violations?: ApiViolation[];
            detail?: string;
            "hydra:description"?: string;
        };
    };
};

/**
 * Human-readable message the API attached to a 403 or 422 answer, `null` when there is none.
 */
export const getApiClientErrorMessage = (error: unknown): string | null => {
    if (!error || typeof error !== "object") return null;
    const response = (error as ApiErrorEnvelope).response;
    if (response?.status !== 403 && response?.status !== 422) return null;

    const violationMessages = (response.data?.violations ?? [])
        .map((violation) => violation.message)
        .filter((message): message is string => typeof message === "string" && message !== "");
    if (violationMessages.length > 0) return violationMessages.join("\n");

    const description = response.data?.detail ?? response.data?.["hydra:description"];
    return typeof description === "string" && description !== "" ? description : null;
};
